import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, WORKER_API_BASE } from '../lib/api';
import { tr } from '../lib/i18nHelpers';
import { Upload, Loader2, AlertCircle, Clock } from 'lucide-react';

interface BundleRow {
  id: string;
  status: string;
  label: string | null;
  pages_total: number | null;
  pages_done: number | null;
  docs_total: number | null;
  docs_done: number | null;
  links_proposed: number | null;
  links_confirmed: number | null;
  error: string | null;
  created_at: string | null;
  updated_at: string | null;
}

const STATUS_PILL: Record<string, { cls: string; labelEn: string; zh: string; cn: string }> = {
  queued: { cls: 'bg-gray-100 text-gray-600 border border-gray-200', labelEn: 'Queued', zh: '排隊中', cn: '排队中' },
  segmenting: { cls: 'bg-gray-100 text-gray-600 border border-gray-200', labelEn: 'Segmenting', zh: '分段中', cn: '分段中' },
  intake: { cls: 'bg-blue-50 text-blue-700 border border-blue-200', labelEn: 'Intake', zh: '收錄中', cn: '收录中' },
  matching: { cls: 'bg-blue-50 text-blue-700 border border-blue-200 animate-pulse', labelEn: 'Matching', zh: '配對中', cn: '配对中' },
  ready_for_review: { cls: 'bg-amber-50 text-amber-700 border border-amber-200', labelEn: 'Ready for review', zh: '待審核', cn: '待审核' },
  posted: { cls: 'bg-green-50 text-green-700 border border-green-200', labelEn: 'Posted', zh: '已入帳', cn: '已入账' },
  partial: { cls: 'bg-amber-50 text-amber-700 border border-amber-200', labelEn: 'Partial', zh: '部分完成', cn: '部分完成' },
  error: { cls: 'bg-red-50 text-red-700 border border-red-200', labelEn: 'Error', zh: '錯誤', cn: '错误' },
};

function StatusPill({ status }: { status: string }) {
  const p = STATUS_PILL[status] || STATUS_PILL.queued;
  return (
    <span className={`inline-block text-[11px] font-medium px-2 py-0.5 rounded-full ${p.cls}`}>
      {tr(p.labelEn, p.zh, p.cn)}
    </span>
  );
}

// 6-node mini stepper; the current node's index comes from the bundle status.
const STEP_KEYS = ['queued', 'segmenting', 'intake', 'matching', 'review', 'posted'];

function statusStepIndex(status: string): number {
  switch (status) {
    case 'queued': return 0;
    case 'segmenting': return 1;
    case 'intake': return 2;
    case 'matching': return 3;
    case 'ready_for_review': return 4;
    case 'posted': case 'partial': case 'error': return 5;
    default: return 0;
  }
}

function MiniStepper({ status }: { status: string }) {
  const current = statusStepIndex(status);
  const labels: [string, string, string][] = [
    ['Upload', '上傳', '上传'],
    ['Segment', '分段', '分段'],
    ['Intake', '收錄', '收录'],
    ['Match', '配對', '配对'],
    ['Review', '審核', '审核'],
    ['Posted', '入帳', '入账'],
  ];
  return (
    <div className="flex items-center gap-1 mt-2">
      {STEP_KEYS.map((k, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <div key={k} className="flex items-center gap-1 flex-1 min-w-0">
            <div className={`h-1 flex-1 rounded-full ${done ? 'bg-green-500' : active ? 'bg-primary' : 'bg-border'}`} />
          </div>
        );
      })}
      <span className="text-[10px] text-muted-foreground ml-2 whitespace-nowrap">
        {tr(labels[Math.min(current, 5)][0], labels[Math.min(current, 5)][1], labels[Math.min(current, 5)][2])}
      </span>
    </div>
  );
}

export default function Bundles() {
  useTranslation(); // keep tr() updates live on language change
  const nav = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['bundles'],
    queryFn: () => api('/bundles', { baseUrl: WORKER_API_BASE }),
    refetchInterval: 10_000,
  });
  const bundles: BundleRow[] = data?.bundles || [];

  const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
  const isStale = (b: BundleRow) =>
    b.status === 'ready_for_review' &&
    b.created_at &&
    new Date(b.created_at.replace(' ', 'T') + (b.created_at.includes('Z') ? '' : 'Z')).getTime() < sevenDaysAgo;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">{tr('Collections', '文件批次', '文件批次')}</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {tr('Bank document collections — upload a pile of statements, invoices and receipts together; they are segmented, matched and posted as one bundle.', '銀行文件批次 — 一次上傳整疊月結單、發票及收據；系統自動分段、配對並入帳。', '银行文件批次 — 一次上传整叠月结单、发票及收据；系统自动分段、配对并入账。')}
          </p>
        </div>
        <button onClick={() => nav('/file-upload?channel=bank_document_collection')}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:opacity-90">
          <Upload className="h-4 w-4" />
          {tr('New collection', '新文件批次', '新文件批次')}
        </button>
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 text-muted-foreground py-8 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" /> {tr('Loading collections…', '載入文件批次…', '载入文件批次…')}
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-4 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          {(error as any)?.message || tr('Failed to load collections.', '載入文件批次失敗。', '载入文件批次失败。')}
        </div>
      )}

      {!isLoading && !error && bundles.length === 0 && (
        <div className="bg-card border rounded-lg p-8 text-center text-sm text-muted-foreground">
          {tr('No collections yet. Click "New collection" to upload your first bank document batch.', '暫無文件批次。點擊「新文件批次」上傳第一批銀行文件。', '暂无文件批次。点击「新文件批次」上传第一批银行文件。')}
        </div>
      )}

      <div className="space-y-3">
        {bundles.map(b => (
          <div key={b.id}
            onClick={() => nav(`/bundles/review/${b.id}`)}
            className="bg-card border rounded-lg p-4 cursor-pointer hover:bg-muted/40 transition-colors">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-semibold text-sm">{b.label || b.id}</span>
              <StatusPill status={b.status} />
              {isStale(b) && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                  <Clock className="h-3 w-3" />
                  {tr('Draft — unreviewed over a week', '草稿 — 超過一星期未審核', '草稿 — 超过一星期未审核')}
                </span>
              )}
              <span className="ml-auto text-xs text-muted-foreground">
                {b.created_at ? String(b.created_at).slice(0, 16).replace('T', ' ') : ''}
              </span>
            </div>
            <div className="text-xs text-muted-foreground flex gap-4 mt-1.5">
              <span>{(b.pages_done ?? 0)}/{b.pages_total ?? 0} {tr('pages', '頁', '页')}</span>
              <span>{(b.docs_done ?? 0)}/{b.docs_total ?? 0} {tr('documents', '份文件', '份文件')}</span>
              {b.error && (
                <span className="text-red-600 dark:text-red-400 truncate max-w-xs" title={b.error}>
                  {b.error}
                </span>
              )}
            </div>
            <MiniStepper status={b.status} />
          </div>
        ))}
      </div>
    </div>
  );
}
